import * as React from 'react';

import { faLoader } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  'vai-font-inter vai-inline-flex vai-items-center vai-justify-center vai-gap-2 vai-whitespace-nowrap vai-text-sm vai-font-medium vai-ring-offset-background vai-transition-colors focus-visible:vai-outline-none focus-visible:vai-ring-2 focus-visible:vai-ring-ring focus-visible:vai-ring-offset-2 disabled:vai-pointer-events-none disabled:vai-opacity-50 [&_svg]:vai-pointer-events-none [&_svg]:vai-size-4 [&_svg]:vai-shrink-0',
  {
    variants: {
      variant: {
        default:
          'vai-bg-sapphire-600 vai-border-none vai-font-semibold vai-text-white hover:vai-bg-sapphire-600/90',
        success:
          'vai-bg-green-500 vai-border-none vai-font-semibold vai-text-white hover:vai-bg-green-500/90',
        insurance: 'vai-bg-white vai-border-none',
        destructive:
          'vai-bg-destructive vai-text-destructive-foreground hover:vai-bg-destructive/90',
        outline:
          'vai-border vai-border-input vai-bg-background hover:vai-bg-accent hover:vai-text-accent-foreground',
        secondary:
          'vai-bg-secondary vai-text-secondary-foreground hover:vai-bg-secondary/80',
        ghost: 'hover:vai-bg-accent hover:vai-text-accent-foreground',
        ghostIcon:
          'vai-bg-transparent vai-text-gray-800 vai-border-none vai-p-0 vai-m-0 hover:vai-bg-grey-200 focus:vai-ring-0 focus-visible:vai-ring-0 focus-visible:vai-ring-offset-0 focus:vai-ring-transparent focus-visible:vai-ring-transparent focus:vai-outline-none focus-visible:vai-outline-none active:vai-ring-0 active:vai-outline-none focus-within:vai-ring-0 focus-within:vai-outline-none focus-within:vai-ring-transparent',
        link: 'vai-text-primary vai-underline-offset-4 vai-text-wrap hover:vai-underline',
        icon: 'vai-bg-transparent vai-border-none vai-p-0 vai-m-0 hover:vai-bg-[#1F3D66] hover:vai-text-white focus:vai-ring-0 focus-visible:vai-ring-0 focus-visible:vai-ring-offset-0 focus:vai-ring-transparent focus-visible:vai-ring-transparent focus:vai-outline-none focus-visible:vai-outline-none active:vai-ring-0 active:vai-outline-none focus-within:vai-ring-0 focus-within:vai-outline-none focus-within:vai-ring-transparent',
        completed:
          'vai-bg-green-500 vai-border-none vai-font-semibold vai-text-primary-foreground hover:vai-bg-green-500/90',
        sheet:
          'vai-bg-transparent vai-border-none vai-outline vai-outline-slate-200 vai-text-black hover:vai-bg-accent',
        pulse:
          'vai-border vai-border-blue-500 vai-text-white vai-bg-sapphire-600 vai-animate-pulse-border',
        destructivePulse:
          'vai-bg-destructive vai-text-destructive-foreground hover:vai-bg-destructive/90 vai-animate-pulse-border',
      },

      size: {
        default: 'vai-h-10 vai-rounded-md vai-px-4 vai-py-2',
        sm: 'vai-h-9 vai-rounded-md vai-px-3',
        lg: 'vai-h-11 vai-rounded-md vai-px-8',
        icon: 'vai-h-10 vai-w-10 vai-rounded-full vai-px-0',
        link: 'vai-h-10 vai-w-10 vai-rounded-full vai-px-0',
        xs: 'vai-h-7 vai-rounded-md vai-px-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  disabled?: boolean;
  asChild?: boolean;
  dataTestId?: string;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      asChild = false,
      disabled = false,
      dataTestId,
      loading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    const content = (
      <>
        {loading && (
          <FontAwesomeIcon
            icon={faLoader}
            className="vai-animate-spin"
            aria-hidden="true"
          />
        )}
        {children}
      </>
    );

    // When asChild is true, wrap in span to ensure single child (Slot requires a single element, not a Fragment)
    const wrappedContent = asChild ? (
      <span className="vai-inline-flex vai-items-center vai-gap-2">
        {content}
      </span>
    ) : (
      content
    );

    return (
      <Comp
        data-testid={dataTestId}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={isDisabled}
      >
        {wrappedContent}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
